// eslint-disable no-non-orangelogic-images-eslint
import { type ReactElement } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { FeaturedInfoCard } from '@/components/adg4/featured-info-card/src/featured-info-card';
import {
	FeaturedInfoHeader,
	LAUNCH,
	PLAN,
	REPORT,
	TRACK,
	type FeaturedInfoHeaderProps,
} from '@/components/adg4/featured-info-header/src/featured-info-header';
import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

const featuresSectionStyles = css({
	background: wacToken('color.neutral.light100'),
	padding: '48px 0',
});

const featuresGridStyles = css({
	display: 'grid',
	position: 'relative',
	gridTemplateColumns: '1fr',
	gap: '32px',
	margin: '0 32px',

	[media.above.sm]: {
		maxWidth: 'min(1168px, calc(100% - 120px))',
		margin: '0 auto',
		gridTemplateColumns: 'repeat(2, 1fr)',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div:first-of-type:has(~ div:last-of-type:nth-of-type(odd))': {
			gridColumn: 'span 2',
		},
	},
});

export type FeaturedInfoSectionProps = FeaturedInfoHeaderProps & {
	children: ReactElement<typeof FeaturedInfoCard> | ReactElement<typeof FeaturedInfoCard>[];
};

export { PLAN, TRACK, LAUNCH, REPORT };

export const FeaturedImages = {
	collaborateAutomateWorkflows:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/collaborate-automate-workflows.webp',
	collaborateShareKeyResources:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/collaborate-share-key-resources.webp',
	collaborateStreamlineRequests:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/jswf-collaborate-streamline-requests-2.webp',
	planAlignWorkToGoals:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/plan-align-work-to-goals.webp',
	planGetTheBigPicture:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/plan-get-the-big-picture.webp',
	planJumpstartWithExpertTemplates:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/plan-jumpstart-with-expert-templates.webp',
	planPlanAndAssignWork:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/plan-plan-and-assign-work.webp',
	reportLearnAsYouDeliver:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/report-learn-as-you-deliver-2.webp',
	reportShareRealTimeUpdates:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/report-share-real-time-updates.webp',
	reportTrackYourProgressToGoals:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/report-track-your-progress-to-goals-2.webp',
	reportUnderstandTeamPerformance:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/report-understand-team-performance-2.webp',
	trackManageDependencies:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/track-manage-dependencies.webp',
	trackMonitorProgress:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/track-monitor-progress.webp',
	trackRemoveManualWork:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/track-remove-manual-work.webp',
	trackTrackWorkWhereYouWork:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/track-track-work-where-you-work.webp',
	streamlineRequests:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/streamline-requests.webp',
	shareKeyResources:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/jswf/share-key-resources.webp',
};

export const FeaturedInfoSection = ({
	variant,
	eyebrow,
	header,
	description,
	children,
}: FeaturedInfoSectionProps) => {
	return (
		<section css={featuresSectionStyles}>
			<TypographyStyles>
				<FeaturedInfoHeader
					variant={variant}
					eyebrow={eyebrow}
					header={header}
					description={description}
				/>
				<div css={featuresGridStyles}>{children}</div>
			</TypographyStyles>
		</section>
	);
};
